import { useMutation } from "@apollo/client";
import {
    Button,
    Dialog,
    DialogBody,
    DialogFooter,
    FormGroup,
    HTMLSelect,
    HTMLTable,
    InputGroup,
    NumericInput,
    OverlayToaster,
    Position,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
    segmentTrackAdvancedStudiesNewAdvancedStudyDialogClosed,
    segmentTrackAdvancedStudiesNewAdvancedStudyDialogCreateStudy
} from "analytics/analyticTrackEvents";
import { useAdvancedStudyContext } from "contexts/AdvancedStudyContext/AdvancedStudyContext";
import useMapViewConfiguration from "contexts/MapViewConfigurationContext/hooks/useMapViewConfiguration";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    CreateAdvancedStudyMutation,
    CreateAdvancedStudyMutationVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import {
    convertAdvancedStudyLoadsToHasuraLoads,
    DEFAULT_MW_SIZE,
    FacilityType
} from "types/advancedStudyType";
import { niraBusIdToBusId } from "types/busType";
import "./NewAdvancedStudyDialog.scss";

const CREATE_ADVANCED_STUDY = loader("src/graphql/createAdvancedStudy.graphql");
const GET_ADVANCED_STUDIES = loader("src/graphql/getAdvancedStudies.graphql");

const AdvancedStudyToaster = OverlayToaster.create({
    position: Position.TOP
});

const LoadInput = ({
    type,
    mwSize,
    onUpdate,
    onRemove,
    onChangeType,
    typeOptions
}: {
    type: FacilityType;
    mwSize: number;
    onUpdate: (value: number) => void;
    onRemove: () => void;
    onChangeType: (newType: FacilityType) => void;
    typeOptions: { type: FacilityType; disabled: boolean }[];
}) => {
    return (
        <div className="NewAdvancedStudyDialog-load-row">
            <HTMLSelect
                value={type}
                onChange={(e) => onChangeType(e.target.value as FacilityType)}
            >
                {typeOptions.map((opt) => (
                    <option
                        key={opt.type}
                        value={opt.type}
                        disabled={opt.disabled}
                    >
                        {opt.type}
                    </option>
                ))}
            </HTMLSelect>
            <NumericInput
                value={mwSize}
                onValueChange={onUpdate}
                min={1}
                max={5000}
                stepSize={10}
            />
            <Tooltip content={"Remove Load"}>
                <Button
                    icon={IconNames.CROSS}
                    variant="minimal"
                    intent="danger"
                    onClick={onRemove}
                />
            </Tooltip>
        </div>
    );
};

const NewAdvancedStudyDialog = () => {
    const {
        busFiltersConfiguration: {
            busFilters: { scope }
        }
    } = useMapViewConfiguration();
    const { region } = useMapViewRoutingMetadata();
    const { teamId } = useUserDataContext();

    const {
        isNewStudyDialogOpen,
        closeNewStudyDialog,
        enterAddMoreLoadMode,
        studyTitle,
        setStudyTitle,
        studyBuses,
        updateBusAndMaybeRemove,
        resetStudy
    } = useAdvancedStudyContext();

    const [createAdvancedStudy, { loading }] = useMutation<
        CreateAdvancedStudyMutation,
        CreateAdvancedStudyMutationVariables
    >(CREATE_ADVANCED_STUDY);

    const validateStudy = (): {
        isValid: boolean;
        errorMessage?: string;
    } => {
        if (!studyTitle.trim()) {
            return {
                isValid: false,
                errorMessage: "Enter a title to create the study"
            };
        }
        if (studyBuses.length === 0) {
            return {
                isValid: false,
                errorMessage: "At least one bus is required"
            };
        }
        if (!studyBuses.some((bus) => bus.studyMwSize !== undefined)) {
            return {
                isValid: false,
                errorMessage: "At least one study load is required"
            };
        }
        for (const bus of studyBuses) {
            const mwValues = [bus.benchMwSize, bus.studyMwSize].filter(
                (v): v is number => v !== undefined
            );
            for (const mw of mwValues) {
                if (mw <= 0 || mw > 5000) {
                    return {
                        isValid: false,
                        errorMessage: "All MW values must be between 1 and 5000"
                    };
                }
            }
        }
        return { isValid: true };
    };

    const validation = validateStudy();
    const canSubmit = validation.isValid;

    const createAdvancedStudyHandler = async () => {
        const advancedStudyLoads =
            convertAdvancedStudyLoadsToHasuraLoads(studyBuses);
        const createAdvancedStudyVariables = {
            title: studyTitle,
            advanced_study_loads: advancedStudyLoads,
            team_id: teamId,
            region,
            scope
        };
        const refetchQueries = [
            {
                query: GET_ADVANCED_STUDIES,
                variables: { scope }
            }
        ];
        createAdvancedStudy({
            variables: createAdvancedStudyVariables,
            refetchQueries: refetchQueries,
            onCompleted: (data: CreateAdvancedStudyMutation) => {
                segmentTrackAdvancedStudiesNewAdvancedStudyDialogCreateStudy(
                    data.insert_advanced_studies_one?.advanced_study_id
                );
                closeNewStudyDialog();
                AdvancedStudyToaster.show({
                    message: 'Advanced study "' + studyTitle + '" requested!',
                    intent: "success"
                });
            }
        });
    };

    const onClose = () => {
        segmentTrackAdvancedStudiesNewAdvancedStudyDialogClosed();
        closeNewStudyDialog();
    };

    const sortedBuses = [...studyBuses].sort((a, b) =>
        a.niraBusId.localeCompare(b.niraBusId)
    );

    return (
        <Dialog
            isOpen={isNewStudyDialogOpen}
            onClose={onClose}
            title="New Advanced Study"
            className="NewAdvancedStudyDialog"
            isCloseButtonShown={true}
        >
            <DialogBody className="NewAdvancedStudyDialog-dialog-body">
                <div className="NewAdvancedStudyDialog-study-title">
                    <FormGroup label="Title" labelFor="study-title" inline>
                        <InputGroup
                            id="study-title"
                            value={studyTitle}
                            onChange={(e) => setStudyTitle(e.target.value)}
                            placeholder={"Enter study title..."}
                            className="NewAdvancedStudyDialog-study-title-input"
                        />
                    </FormGroup>
                    <Button
                        icon={IconNames.RESET}
                        intent="danger"
                        variant="minimal"
                        onClick={resetStudy}
                    >
                        Reset
                    </Button>
                </div>

                <div className="NewAdvancedStudyDialog-table-wrapper">
                    <HTMLTable className="NewAdvancedStudyDialog-table" striped>
                        <thead>
                            <tr>
                                <th>Bus #</th>
                                <th>Bus Name</th>
                                <th>Type</th>
                                <th>MW Size</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedBuses.map((bus) => (
                                <tr key={bus.niraBusId}>
                                    <td>{niraBusIdToBusId(bus.niraBusId)}</td>
                                    <td>{bus.busDisplayName}</td>
                                    <td colSpan={3}>
                                        {bus.benchMwSize !== undefined && (
                                            <LoadInput
                                                type="Bench Load"
                                                mwSize={bus.benchMwSize}
                                                onUpdate={(value) =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        benchMwSize: value
                                                    })
                                                }
                                                onRemove={() =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        benchMwSize: undefined
                                                    })
                                                }
                                                onChangeType={() =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        studyMwSize:
                                                            bus.benchMwSize,
                                                        benchMwSize: undefined
                                                    })
                                                }
                                                typeOptions={[
                                                    {
                                                        type: "Bench Load",
                                                        disabled: false
                                                    },
                                                    {
                                                        type: "Study Load",
                                                        disabled:
                                                            bus.studyMwSize !==
                                                            undefined
                                                    }
                                                ]}
                                            />
                                        )}
                                        {bus.studyMwSize !== undefined && (
                                            <LoadInput
                                                type="Study Load"
                                                mwSize={bus.studyMwSize}
                                                onUpdate={(value) =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        studyMwSize: value
                                                    })
                                                }
                                                onRemove={() =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        studyMwSize: undefined
                                                    })
                                                }
                                                onChangeType={() =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        benchMwSize:
                                                            bus.studyMwSize,
                                                        studyMwSize: undefined
                                                    })
                                                }
                                                typeOptions={[
                                                    {
                                                        type: "Bench Load",
                                                        disabled:
                                                            bus.benchMwSize !==
                                                            undefined
                                                    },
                                                    {
                                                        type: "Study Load",
                                                        disabled: false
                                                    }
                                                ]}
                                            />
                                        )}
                                        {(bus.benchMwSize === undefined ||
                                            bus.studyMwSize === undefined) && (
                                            <Button
                                                icon={IconNames.ADD}
                                                variant="minimal"
                                                size="small"
                                                onClick={() =>
                                                    updateBusAndMaybeRemove({
                                                        ...bus,
                                                        ...(bus.benchMwSize ===
                                                        undefined
                                                            ? {
                                                                  benchMwSize:
                                                                      DEFAULT_MW_SIZE
                                                              }
                                                            : {
                                                                  studyMwSize:
                                                                      DEFAULT_MW_SIZE
                                                              })
                                                    })
                                                }
                                            >
                                                {bus.benchMwSize === undefined
                                                    ? "Add Bench Load"
                                                    : "Add Study Load"}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </HTMLTable>
                </div>
            </DialogBody>
            <DialogFooter
                actions={
                    <>
                        <Button icon={IconNames.CROSS} onClick={onClose}>
                            Close
                        </Button>
                        <Tooltip
                            content={validation.errorMessage}
                            position={Position.TOP}
                            disabled={canSubmit}
                        >
                            <Button
                                icon={IconNames.SEND_MESSAGE}
                                intent="primary"
                                onClick={createAdvancedStudyHandler}
                                disabled={loading || !canSubmit}
                            >
                                Create Study
                            </Button>
                        </Tooltip>
                    </>
                }
            >
                <Button
                    icon={IconNames.ADD}
                    variant="outlined"
                    onClick={enterAddMoreLoadMode}
                >
                    Add more load
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

export default NewAdvancedStudyDialog;
